// src/pages/admin/BusinessLogViewer.js - RESPONSIVE
import React, { useState, useEffect } from 'react';
import { Calendar, User, Building, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../Services/api';

const BusinessLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter state - only log type
  const [logTypeFilter, setLogTypeFilter] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/Client/GetBusinessInteractionLog');
      
      if (response.data?.StatusCode === 200 && response.data.ResultSet) {
        const formattedLogs = response.data.ResultSet.map(log => ({
          log_id: log.sbl_log_id || `log-${Date.now()}-${Math.random()}`,
          client_id: log.client_id || log.sbl_client_id || log.scm_client_id,
          client_name: log.client_name || log.scm_client_name || 'Unknown Client',
          business_id: log.business_id || log.sbl_business_id || log.sbm_business_id,
          business_name: log.business_name || log.sbm_business_name || 'Unknown Business',
          contact_person: log.contact_person || log.sbl_contact_person || 'Not specified',
          log_date: log.log_date || log.sbl_log_date || new Date().toISOString(),
          log_type: log.log_type || log.sbl_log_type || 'general',
          log_subject: log.log_subject || log.sbl_log_subject || 'No Subject',
          log_notes: log.log_notes || log.sbl_log_notes || 'No notes available'
        }));
        
        setLogs(formattedLogs);
        setFilteredLogs(formattedLogs);
      } else {
        setLogs([]);
        setFilteredLogs([]);
        if (response.data?.Result) {
          setError(response.data.Result);
        }
      }
    } catch (error) {
      console.error('Error loading business logs:', error);
      setError('Failed to load business logs. Please try again.');
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  // Apply log type filter
  useEffect(() => {
    if (!logTypeFilter) {
      setFilteredLogs(logs);
    } else {
      const filtered = logs.filter(log => 
        log.log_type.toLowerCase() === logTypeFilter.toLowerCase()
      );
      setFilteredLogs(filtered);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [logs, logTypeFilter]);

  // Get unique log types for filter dropdown
  const getUniqueLogTypes = () => {
    const types = [...new Set(logs.map(log => log.log_type))];
    return types.filter(type => type && type !== 'general');
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getLogTypeColor = (logType) => {
    const type = (logType || '').toLowerCase();
    switch (type) {
      case 'call':
        return 'bg-blue-100 text-blue-800';
      case 'meeting':
        return 'bg-green-100 text-green-800';
      case 'email':
        return 'bg-purple-100 text-purple-800';
      case 'visit':
        return 'bg-yellow-100 text-yellow-800';
      case 'note':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header Section - Fixed: Removed unwanted login text */}
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">Business Interaction Logs</h1>
        <p className="text-gray-500 text-sm md:text-base mt-1">View all client communications and business activities</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm md:text-base">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Table Section with Pagination */}
      <div className="bg-white rounded-lg shadow overflow-hidden h-full flex flex-col">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center flex-shrink-0 gap-3">
          <div className="text-sm text-gray-600">
            Total logs: {filteredLogs.length}
            {logTypeFilter && ` (filtered by: ${logTypeFilter})`}
          </div>
          
          {/* Log Type Filter Dropdown in corner */}
          <div className="flex items-center space-x-2 w-full md:w-auto">
            <label className="text-sm text-gray-700 whitespace-nowrap">Filter by type:</label>
            <select
              value={logTypeFilter}
              onChange={(e) => setLogTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full md:w-auto"
            >
              <option value="">All Types</option>
              {getUniqueLogTypes().map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="p-8 text-center flex-1 flex items-center justify-center">
            <div>
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="mt-2 text-gray-600 text-sm md:text-base">Loading business logs...</p>
            </div>
          </div>
        ) : currentLogs.length > 0 ? (
          <>
            <div className="flex-1 min-h-0 overflow-auto">
              <div className="min-w-[1024px] md:min-w-0">
                <table className="min-w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Person</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentLogs.map((log, index) => (
                      <tr key={log.log_id || index} className="hover:bg-gray-50">
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <Calendar size={14} className="mr-2 text-gray-400 hidden md:block" />
                            <span className="text-xs md:text-sm">{formatDate(log.log_date)}</span>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User size={14} className="mr-2 text-gray-400 hidden md:block" />
                            <span className="font-medium text-gray-900 text-xs md:text-sm">{log.client_name}</span>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building size={14} className="mr-2 text-gray-400 hidden md:block" />
                            <span className="text-gray-900 text-xs md:text-sm">{log.business_name}</span>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                          {log.contact_person}
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getLogTypeColor(log.log_type)}`}>
                            {log.log_type}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900">
                          <div className="max-w-[150px] md:max-w-xs truncate" title={log.log_subject}>
                            {log.log_subject}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-900">
                          <div className="max-w-[150px] md:max-w-xs line-clamp-2" title={log.log_notes}>
                            {log.log_notes}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Pagination Controls - Added: Pagination */}
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length} entries
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="5">5 per page</option>
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded border ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm md:text-base ${currentPage === pageNum ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded border ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500 flex-1 flex items-center justify-center">
            <div>
              <p className="text-lg font-medium mb-2">No business logs found</p>
              <p className="text-sm text-gray-400">
                {logTypeFilter ? 'Try selecting a different log type' : 'Check if logs exist in the database'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessLogViewer;